// The Admin configuration console's own words (lane B10 S20a; CR-066 "Change -> Validate -> Version -> Activate ->
// Audit"; plan D14, D17). Every entry quotes the vendored glossary or design-system excerpt it is taken from. Words the
// shared modules already carry are imported where they are drawn, never repeated here: the area names *Statuses* and
// *Reason lists*, *Effective version*, *Current*, *Previous version*, *New version* (G6_LABELS); *Show previous
// versions*, *Edit*, *View history* (SURFACE_LABELS); the journey *Change · Validate · Version · Activate · Audit*
// (JOURNEY_LABELS); *Approve*, *Validate*, *Submit*, *Cancel* (ACTION_LABELS); *Reason*, *Actor*, *Timestamp*
// (HISTORY_LABELS); *Required* (COMMON_LABELS); the version statuses *Draft*, *Scheduled*, *Rejected* (the G §5, §9,
// §10 status chains).

import { defineLabels } from "@/mc/labels";

const CONSOLE = "\"CR-066 TA Admin configuration console\"";
const CFG_KINDS = "Statuses · Reason lists · Taxonomies · Thresholds · Reminders · Templates · Segments · Strategies · Effective version";
const CONFIG_VERSION = "\"historical records retain configuration version used.\"";
const FILTERS = "\"Support date, recruiter, coordinator, HRBP, HM, function, department, brand, job family, area of interest, level, location, strategy, new/replacement, status, phase, health";
const REQ_STATES = "\"Additional states: On Hold, Cancellation Pending/Cancelled, Reopen Pending/Reopened.\"";
const MILESTONE = "\"Offer Accepted is a milestone, not Filled.\"";
const REPORTING_CATEGORY = "| Reporting category | \"final notes/reporting category\" required for cancellation.";

/** The page, its areas beyond the shared two, the framework link, and the controls of the list editor. */
export const CONSOLE_LABELS = /* @__PURE__ */ defineLabels({
  // his CR-066 name with the Admin word (DC-228 folds his TA Admin into the one Admin)
  console: { text: "Admin configuration console" },
  configurationVersion: { text: "Configuration version" },
  taxonomies: { text: "Taxonomies" },
  segments: { text: "Segments" },
  strategies: { text: "Strategies" },
  taFramework: { text: "TA framework" },
  reject: { text: "Reject" },
  add: { text: "Add" },
  active: { text: "Active" },
  status: { text: "Status" },
  notes: { text: "Notes" },
});

/** The name of each list a list area holds, by its configuration key's name: the status machines, the reason lists and
 * the taxonomies (G §5, §7, §8, §14). A list a later lane registers without a word here is shown by its key. */
export const LIST_LABELS = /* @__PURE__ */ defineLabels({
  request: { text: "Request" },
  requisition: { text: "Requisition" },
  candidateApplication: { text: "Candidate application" 



},
  offer: { text: "Offer" },
  milestone: { text: "Milestone" },
  onHold: { text: "On Hold" },
  holdReason: { text: "Hold reason" },
  withdrawalReason: { text: "Withdrawal reason" 

},
  reopen: { text: "Reopen" },
  reportingCategory: { text: "Reporting category" },
  declineReasons: { text: "Decline reasons" },
  function: { text: "Function" },
  jobFamily: { text: "Job family" },
  areaOfInterest: { text: "Area of interest" },
  location: { text: "Location" },
  source: { text: "Source" },
  segment: { text: "Segment" },
  postingStrategy: { text: "Posting strategy" 

},
});

/** Configuration key → the list's name (B1's registrations in `tools/config.py`; plan D14's areas). */
export const KEY_WORDS: Readonly<Record<string, string>> = Object.freeze({
  "status_labels:request": LIST_LABELS.request,
  "status_labels:requisition": LIST_LABELS.requisition,
  "status_labels:application": LIST_LABELS.candidateApplication,
  "status_labels:offer": LIST_LABELS.offer,
  "status_labels:milestone": LIST_LABELS.milestone,
  "reasons:requisition_hold": LIST_LABELS.onHold,
  "reasons:application_hold": LIST_LABELS.holdReason,
  "reasons:withdrawal": LIST_LABELS.withdrawalReason,
  "reasons:reopen": LIST_LABELS.reopen,
  "reasons:cancel_category": LIST_LABELS.reportingCategory,
  "reasons:offer_decline": LIST_LABELS.declineReasons,
  "taxonomies:function": LIST_LABELS.function,
  "taxonomies:job_family": LIST_LABELS.jobFamily,
  "taxonomies:area_of_interest": LIST_LABELS.areaOfInterest,
  "taxonomies:location": LIST_LABELS.location,
  "taxonomies:source": LIST_LABELS.source,
  "taxonomies:segment": LIST_LABELS.segment,
  "taxonomies:posting_strategy": LIST_LABELS.postingStrategy,
});

/** The list's name for a configuration key, or undefined when no word is registered for it (the key is then shown). */
export function keyWord(configKey: string): string | undefined {
  return KEY_WORDS[configKey];
}
